import {
  createAsyncThunk,
  createSelector,
  createSlice,
} from "@reduxjs/toolkit";
import type { PayloadAction } from "@reduxjs/toolkit";

import { toast } from "react-toastify";

import { ReduxRootState } from "@/types/redux-store";
import { resolveReduxAppStore } from "@/entities/lib/redux-app-store";
import {
  addAccount,
  selectAccountById,
  selectAllAccounts,
} from "@/entities/accounts/table";
import { getAccountsCounts } from "@/selectors/skedCoreSelectors";
import { selectCurrentUser } from "@/entities/current-user";
import { getAccountPlatformType } from "@/entities/accounts/libs/account-platform";
import { getAccountDisplayName } from "@/entities/accounts/libs/get-accounts-name";
import { getUserFriendlyErrorMessage } from "@/api/UserFriendlyError";
import { goToManagePlan } from "@/utils/manage-plan";
import { getAccountsGroups } from "@/selectors/skedCoreSelectors";
import {
  fetchAccountGroups,
  updateAccountGroups,
} from "@/features/account-groups-form/accountsGroups.model";
import { refreshAccountsList } from "@/features/account-groups-form/utils";
import { PlatformNameToPlatformTypeMap } from "@/features/post/format-post";
import { getAccounts, setAccountsCounts } from "@/actions/skedCore";
import { SocialAccountType } from "./SocialAccountType";
import { SocialAccountConnectQueryParams } from "./SocialAccountConnectQueryParams";
import { AccountType } from "@/shared/types/accounts";
import {
  getLocalStorageAddAccountToGroup,
  getLocalStorageHomeSelectedGroupName,
  setLocalStorageAddAccountToGroup,
} from "@/libs/storage/adapters";
import { getPinterestRedirect } from "@/api/social-account-connect/pinterest";
import { getTwitterRedirect } from "@/api/social-account-connect/twitter";
import { getTikTokRedirect } from "@/api/social-account-connect/tiktok";
import { redirectToThreadsAuthPage } from "@/api/social-account-connect/threads/trigger-auth-redirect";
import {
  addInstagramAccount,
  FBPageWithInsta,
  getInstagramAccounts,
  InstagramScopeInboxComments,
} from "@/api/social-account-connect/instagram";
import {
  FacebookGroupsSet,
  FacebookPagesSet,
  FBGroup,
  FBPageExtended,
  addFacebookGroupAccount,
  addFacebookPageAccount,
  getFacebookGroups,
  getFacebookPages,
} from "@/api/social-account-connect/facebook";
import {
  GmbLocation,
  addGmbLocation,
  getAvailableGmbLocations,
  getGmbRedirect,
} from "@/api/social-account-connect/google";
import {
  LinkedInProfile,
  addLinkedInProfile,
  getAvailableLinkedInProfiles,
  getLinkedInRedirect,
} from "@/api/social-account-connect/linkedin";
import {
  YoutubeChannel,
  addYtChannel,
  getAvailableYtChannels,
  getYtRedirect,
} from "@/api/social-account-connect/youtube";
import { connectTikTokAccount } from "@/api/social-account-connect/tiktok";
import { connectThreadsNotificationAccount } from "@/api/social-account-connect/threads/connect-threads-notification-account";
import { connectSnapChatAccount } from "@/api/social-account-connect/shapchat";
import { connectOtherAccount } from "@/api/social-account-connect/other";
import { getImageFromFile } from "@/libs/files";
import { uploadFileToS3 } from "@/api/media-library/upload-file-to-s3";
import { trackEventFx } from "@/libs/analytics";
import {
  ADD_ACCOUNT_CLICKED_FACEBOOK,
  ADD_ACCOUNT_CLICKED_FACEBOOK_GROUP,
  ADD_ACCOUNT_CLICKED_GMB,
  ADD_ACCOUNT_CLICKED_INSTAGRAM,
  ADD_ACCOUNT_CLICKED_INSTAGRAM_PERSONAL,
  ADD_ACCOUNT_CLICKED_LINKEDIN,
  ADD_ACCOUNT_CLICKED_LINKEDIN_PERSONAL,
  ADD_ACCOUNT_CLICKED_OTHER,
  ADD_ACCOUNT_CLICKED_PINTEREST,
  ADD_ACCOUNT_CLICKED_PINTEREST_V3,
  ADD_ACCOUNT_CLICKED_SNAPCHAT,
  ADD_ACCOUNT_CLICKED_THREADS,
  ADD_ACCOUNT_CLICKED_THREADS_NOTIFICATION,
  ADD_ACCOUNT_CLICKED_TIKTOK,
  ADD_ACCOUNT_CLICKED_TWITTER,
  ADD_ACCOUNT_CLICKED_YOUTUBE,
} from "constants/Events";

type WatchListener<T> = (payload: T) => void;
type InstagramSession = Awaited<
  ReturnType<typeof getInstagramAccounts>
>["session"];
type FacebookSession = FacebookPagesSet["session"];

function createWatchableEvent<T>() {
  const listeners = new Set<WatchListener<T>>();

  const fn = ((payload: T) => {
    for (const listener of listeners) {
      listener(payload);
    }
  }) as ((payload: T) => void) & {
    watch: (listener: WatchListener<T>) => () => void;
  };

  fn.watch = (listener: WatchListener<T>) => {
    listeners.add(listener);

    return () => {
      listeners.delete(listener);
    };
  };

  return fn;
}

export function dispatchAppAction(action: unknown) {
  return resolveReduxAppStore()?.dispatch(action);
}

export type CurrentDialog =
  | "main"
  | "add-password"
  | SocialAccountType.Instagram
  | SocialAccountType.Youtube
  | SocialAccountType.FacebookPage
  | SocialAccountType.FacebookGroup
  | SocialAccountType.GoogleMyBusiness
  | SocialAccountType.LinkedInCompany
  | SocialAccountType.LinkedInPersonal
  | SocialAccountType.TikTok
  | SocialAccountType.ThreadsNotification
  | SocialAccountType.ThreadsConnect
  | SocialAccountType.Snapchat
  | SocialAccountType.Other;

export type NotificationAccountType =
  "tiktok" | "snapchat" | "other" | "threads";

type NotificationState = {
  type: NotificationAccountType | null;
  userName: string;
  profilePictureFile: File | null;
  profilePictureUrl: string | null;
  platformTypeOther: string;
  isConnecting: boolean;
};

export type SocialAccountConnectState = {
  currentDialog: CurrentDialog | null;
  startConnectingPending: boolean;
  showHowdyGoDemoDialog: boolean;
  isMainDialogGuarded: boolean;
  selectedAccountId: string | null;
  instagram: {
    data: { session: InstagramSession; pages: FBPageWithInsta[] } | null;
    gettingData: boolean;
    connectingAccountPageId: string | null;
  };
  youtube: {
    channels: YoutubeChannel[] | null;
    isLoggingIn: boolean;
    isGettingChannels: boolean;
    connectingChannelId: string | null;
  };
  facebookPage: {
    data: FacebookPagesSet | null;
    gettingData: boolean;
    connectingFacebookPageId: string | null;
  };
  facebookGroup: {
    data: FacebookGroupsSet | null;
    gettingData: boolean;
    connectingFacebookGroupId: string | null;
  };
  gmb: {
    locations: GmbLocation[] | null;
    isLoggingIn: boolean;
    isGettingChannels: boolean;
    connectingGmbLocationId: string | null;
  };
  linkedin: {
    profiles: LinkedInProfile[] | null;
    isLoggingIn: boolean;
    isGettingEntities: boolean;
    connectingProfileId: string | null;
  };
  notification: NotificationState;
};

const initialState: SocialAccountConnectState = {
  currentDialog: null,
  startConnectingPending: false,
  showHowdyGoDemoDialog: false,
  isMainDialogGuarded: false,
  selectedAccountId: null,
  instagram: {
    data: null,
    gettingData: false,
    connectingAccountPageId: null,
  },
  youtube: {
    channels: null,
    isLoggingIn: false,
    isGettingChannels: false,
    connectingChannelId: null,
  },
  facebookPage: {
    data: null,
    gettingData: false,
    connectingFacebookPageId: null,
  },
  facebookGroup: {
    data: null,
    gettingData: false,
    connectingFacebookGroupId: null,
  },
  gmb: {
    locations: null,
    isLoggingIn: false,
    isGettingChannels: false,
    connectingGmbLocationId: null,
  },
  linkedin: {
    profiles: null,
    isLoggingIn: false,
    isGettingEntities: false,
    connectingProfileId: null,
  },
  notification: {
    type: null,
    userName: "",
    profilePictureFile: null,
    profilePictureUrl: "",
    platformTypeOther: "",
    isConnecting: false,
  },
};

const featureKey = "socialAccountConnect" as const;

const trackEventNames: Partial<Record<SocialAccountType, string>> = {
  [SocialAccountType.Instagram]: ADD_ACCOUNT_CLICKED_INSTAGRAM,
  [SocialAccountType.InstagramPersonal]: ADD_ACCOUNT_CLICKED_INSTAGRAM_PERSONAL,
  [SocialAccountType.FacebookPage]: ADD_ACCOUNT_CLICKED_FACEBOOK,
  [SocialAccountType.FacebookGroup]: ADD_ACCOUNT_CLICKED_FACEBOOK_GROUP,
  [SocialAccountType.LinkedInCompany]: ADD_ACCOUNT_CLICKED_LINKEDIN,
  [SocialAccountType.LinkedInPersonal]: ADD_ACCOUNT_CLICKED_LINKEDIN_PERSONAL,
  [SocialAccountType.GoogleMyBusiness]: ADD_ACCOUNT_CLICKED_GMB,
  [SocialAccountType.Twitter]: ADD_ACCOUNT_CLICKED_TWITTER,
  [SocialAccountType.Youtube]: ADD_ACCOUNT_CLICKED_YOUTUBE,
  [SocialAccountType.Pinterest]: ADD_ACCOUNT_CLICKED_PINTEREST,
  [SocialAccountType.PinterestV3]: ADD_ACCOUNT_CLICKED_PINTEREST_V3,
  [SocialAccountType.TikTok]: ADD_ACCOUNT_CLICKED_TIKTOK,
  [SocialAccountType.ThreadsNotification]:
    ADD_ACCOUNT_CLICKED_THREADS_NOTIFICATION,
  [SocialAccountType.ThreadsConnect]: ADD_ACCOUNT_CLICKED_THREADS,
  [SocialAccountType.Snapchat]: ADD_ACCOUNT_CLICKED_SNAPCHAT,
  [SocialAccountType.Other]: ADD_ACCOUNT_CLICKED_OTHER,
};

function getFeatureState(state: ReduxRootState): SocialAccountConnectState {
  return (
    (
      state as ReduxRootState & {
        [featureKey]?: SocialAccountConnectState;
      }
    )[featureKey] ?? initialState
  );
}

async function redirectTo(url: string) {
  window.location.href = url;
}

function showFriendlyError(error: unknown) {
  // SKE-3462: when the server signals an at-quota refusal with a structured
  // `account_limit_reached` code, navigate straight to Manage Plan with
  // the additional-account addon highlighted. The Manage Plan page
  // explains the context — adding a toast here is dead UI because
  // `goToManagePlan` sets `window.location.href` synchronously and the
  // page begins unloading before any toast can render.
  // Inlined rather than importing `getApiErrorCode` from
  // `@/api/UserFriendlyError` so existing tests that wholesale-mock that
  // module don't need to be updated. `user_not_permitted_to_add_accounts`
  // is intentionally toast-only — only an admin can grant access, so a
  // Manage Plan redirect would be misleading.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const apiErrorCode = (error as any)?.response?.data?.api_error_code;
  if (apiErrorCode === "account_limit_reached") {
    goToManagePlan({ addon: "additional-account" });
    return;
  }
  toast.error(getUserFriendlyErrorMessage(error as Error));
}

function isRedirectedAccountLoaded(
  thunkApi: {
    getState: () => unknown;
  },
  accountId?: string | null
) {
  return Boolean(
    accountId &&
    selectAccountById(thunkApi.getState() as ReduxRootState, accountId)
  );
}

function refreshAccountsListOnce(thunkApi: {
  dispatch: (action: unknown) => unknown;
  getState: () => unknown;
}) {
  return new Promise<void>((resolve) => {
    // The legacy getAccounts action reports completion through a callback.
    // We intentionally resolve on both success and failure so the redirect flow
    // keeps its toast-only UX and can retry without surfacing extra errors.
    thunkApi.dispatch(
      getAccounts(() => {
        resolve();
      }) as never
    );
  });
}

async function refreshAccountsAfterRedirect(
  thunkApi: {
    dispatch: (action: unknown) => unknown;
    getState: () => unknown;
  },
  accountId?: string | null
) {
  await refreshAccountsListOnce(thunkApi);

  if (!accountId) {
    return;
  }

  for (const delayMs of [1500, 5000]) {
    if (isRedirectedAccountLoaded(thunkApi, accountId)) {
      return;
    }

    await new Promise((resolve) => {
      setTimeout(resolve, delayMs);
    });

    if (isRedirectedAccountLoaded(thunkApi, accountId)) {
      return;
    }

    await refreshAccountsListOnce(thunkApi);
  }
}

const socialAccountConnectSlice = createSlice({
  name: featureKey,
  initialState,
  reducers: {
    openDialog(state, action: PayloadAction<CurrentDialog>) {
      state.currentDialog = action.payload;
      state.showHowdyGoDemoDialog = false;
    },
    closeDialogState(state, action: PayloadAction<boolean | undefined>) {
      state.currentDialog = null;
      state.isMainDialogGuarded = false;
      state.selectedAccountId = null;
      state.instagram = initialState.instagram;
      state.youtube = initialState.youtube;
      state.facebookPage = initialState.facebookPage;
      state.facebookGroup = initialState.facebookGroup;
      state.gmb = initialState.gmb;
      state.linkedin = initialState.linkedin;
      state.notification = initialState.notification;

      if (action.payload !== false) {
        setLocalStorageAddAccountToGroup("");
      }
    },
    openHowdyGoDemoDialogState(state) {
      state.showHowdyGoDemoDialog = true;
      state.isMainDialogGuarded = false;
    },
    closeHowdyGoDemoDialogState(state) {
      state.showHowdyGoDemoDialog = false;
    },
    enableMainDialogGuardState(state) {
      state.isMainDialogGuarded = true;
    },
    disableMainDialogGuardState(state) {
      state.isMainDialogGuarded = false;
    },
    setSelectedAccountId(state, action: PayloadAction<string | null>) {
      state.selectedAccountId = action.payload;
    },
    resetInstagramDialogState(state) {
      state.instagram = initialState.instagram;
    },
    resetYoutubeDialogState(state) {
      state.youtube = initialState.youtube;
    },
    resetFacebookPageDialogState(state) {
      state.facebookPage = initialState.facebookPage;
    },
    resetFacebookGroupDialogState(state) {
      state.facebookGroup = initialState.facebookGroup;
    },
    resetGmbDialogState(state) {
      state.gmb = initialState.gmb;
    },
    resetLinkedinDialogState(state) {
      state.linkedin = initialState.linkedin;
    },
    setYoutubeLoggingInState(state, action: PayloadAction<boolean>) {
      state.youtube.isLoggingIn = action.payload;
    },
    setGmbLoggingInState(state, action: PayloadAction<boolean>) {
      state.gmb.isLoggingIn = action.payload;
    },
    setGmbLocationsState(state, action: PayloadAction<GmbLocation[] | null>) {
      state.gmb.locations = action.payload;
    },
    setLinkedinLoggingInState(state, action: PayloadAction<boolean>) {
      state.linkedin.isLoggingIn = action.payload;
    },
    setLinkedinProfilesState(
      state,
      action: PayloadAction<LinkedInProfile[] | null>
    ) {
      state.linkedin.profiles = action.payload;
    },
    setNotificationType(
      state,
      action: PayloadAction<NotificationAccountType | null>
    ) {
      state.notification.type = action.payload;
    },
    setNotificationUserName(state, action: PayloadAction<string>) {
      state.notification.userName = action.payload;
    },
    setNotificationProfilePictureFile(
      state,
      action: PayloadAction<File | null>
    ) {
      state.notification.profilePictureFile = action.payload;
    },
    setNotificationProfilePictureUrl(
      state,
      action: PayloadAction<string | null>
    ) {
      state.notification.profilePictureUrl = action.payload;
    },
    setNotificationPlatformTypeOther(state, action: PayloadAction<string>) {
      state.notification.platformTypeOther = action.payload;
    },
    resetNotificationFormState(state) {
      state.notification = initialState.notification;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(startConnectingAccount.pending, (state) => {
        state.startConnectingPending = true;
      })
      .addCase(startConnectingAccount.fulfilled, (state) => {
        state.startConnectingPending = false;
      })
      .addCase(startConnectingAccount.rejected, (state) => {
        state.startConnectingPending = false;
      })
      .addCase(fetchInstagramConnectAccounts.pending, (state) => {
        state.instagram.gettingData = true;
      })
      .addCase(fetchInstagramConnectAccounts.fulfilled, (state, action) => {
        state.instagram.gettingData = false;
        state.instagram.data = action.payload;
      })
      .addCase(fetchInstagramConnectAccounts.rejected, (state) => {
        state.instagram.gettingData = false;
      })
      .addCase(connectInstagramAccount.pending, (state, action) => {
        state.instagram.connectingAccountPageId = action.meta.arg.page.page.id;
      })
      .addCase(connectInstagramAccount.fulfilled, (state) => {
        state.instagram.connectingAccountPageId = null;
      })
      .addCase(connectInstagramAccount.rejected, (state) => {
        state.instagram.connectingAccountPageId = null;
      })
      .addCase(fetchFacebookPagesThunk.pending, (state) => {
        state.facebookPage.gettingData = true;
      })
      .addCase(fetchFacebookPagesThunk.fulfilled, (state, action) => {
        state.facebookPage.gettingData = false;
        state.facebookPage.data = action.payload;
      })
      .addCase(fetchFacebookPagesThunk.rejected, (state) => {
        state.facebookPage.gettingData = false;
      })
      .addCase(connectFacebookPage.pending, (state, action) => {
        state.facebookPage.connectingFacebookPageId = action.meta.arg.page.id;
      })
      .addCase(connectFacebookPage.fulfilled, (state) => {
        state.facebookPage.connectingFacebookPageId = null;
      })
      .addCase(connectFacebookPage.rejected, (state) => {
        state.facebookPage.connectingFacebookPageId = null;
      })
      .addCase(fetchFacebookGroupsThunk.pending, (state) => {
        state.facebookGroup.gettingData = true;
      })
      .addCase(fetchFacebookGroupsThunk.fulfilled, (state, action) => {
        state.facebookGroup.gettingData = false;
        state.facebookGroup.data = action.payload;
      })
      .addCase(fetchFacebookGroupsThunk.rejected, (state) => {
        state.facebookGroup.gettingData = false;
      })
      .addCase(connectFacebookGroup.pending, (state, action) => {
        state.facebookGroup.connectingFacebookGroupId =
          action.meta.arg.group.id;
      })
      .addCase(connectFacebookGroup.fulfilled, (state) => {
        state.facebookGroup.connectingFacebookGroupId = null;
      })
      .addCase(connectFacebookGroup.rejected, (state) => {
        state.facebookGroup.connectingFacebookGroupId = null;
      })
      .addCase(fetchYoutubeChannels.pending, (state) => {
        state.youtube.isLoggingIn = false;
        state.youtube.isGettingChannels = true;
      })
      .addCase(fetchYoutubeChannels.fulfilled, (state, action) => {
        state.youtube.isLoggingIn = false;
        state.youtube.isGettingChannels = false;
        state.youtube.channels = action.payload;
      })
      .addCase(fetchYoutubeChannels.rejected, (state) => {
        state.youtube.isLoggingIn = false;
        state.youtube.isGettingChannels = false;
        state.youtube.channels = null;
      })
      .addCase(connectYtChannel.pending, (state, action) => {
        state.youtube.connectingChannelId = action.meta.arg.id;
      })
      .addCase(connectYtChannel.fulfilled, (state) => {
        state.youtube.connectingChannelId = null;
      })
      .addCase(connectYtChannel.rejected, (state) => {
        state.youtube.connectingChannelId = null;
      })
      .addCase(continueGmbAccountAdding.pending, (state) => {
        state.gmb.isLoggingIn = false;
        state.gmb.isGettingChannels = true;
      })
      .addCase(continueGmbAccountAdding.fulfilled, (state, action) => {
        state.gmb.isLoggingIn = false;
        state.gmb.isGettingChannels = false;
        state.gmb.locations = action.payload;
      })
      .addCase(continueGmbAccountAdding.rejected, (state) => {
        state.gmb.isLoggingIn = false;
        state.gmb.isGettingChannels = false;
        state.gmb.locations = null;
      })
      .addCase(addGmbLocationThunk.pending, (state, action) => {
        state.gmb.connectingGmbLocationId = action.meta.arg.id;
      })
      .addCase(addGmbLocationThunk.fulfilled, (state) => {
        state.gmb.connectingGmbLocationId = null;
      })
      .addCase(addGmbLocationThunk.rejected, (state) => {
        state.gmb.connectingGmbLocationId = null;
      })
      .addCase(continueLiAccountAdding.pending, (state) => {
        state.linkedin.isLoggingIn = false;
        state.linkedin.isGettingEntities = true;
      })
      .addCase(continueLiAccountAdding.fulfilled, (state, action) => {
        state.linkedin.isLoggingIn = false;
        state.linkedin.isGettingEntities = false;
        state.linkedin.profiles = action.payload;
      })
      .addCase(continueLiAccountAdding.rejected, (state) => {
        state.linkedin.isLoggingIn = false;
        state.linkedin.isGettingEntities = false;
        state.linkedin.profiles = null;
      })
      .addCase(addLinkedInProfileThunk.pending, (state, action) => {
        state.linkedin.connectingProfileId = String(action.meta.arg.id);
      })
      .addCase(addLinkedInProfileThunk.fulfilled, (state) => {
        state.linkedin.connectingProfileId = null;
      })
      .addCase(addLinkedInProfileThunk.rejected, (state) => {
        state.linkedin.connectingProfileId = null;
      })
      .addCase(uploadNotificationProfilePicture.fulfilled, (state, action) => {
        state.notification.profilePictureUrl = action.payload;
      })
      .addCase(connectNotificationAccount.pending, (state) => {
        state.notification.isConnecting = true;
      })
      .addCase(connectNotificationAccount.fulfilled, (state) => {
        state.notification.isConnecting = false;
      })
      .addCase(connectNotificationAccount.rejected, (state) => {
        state.notification.isConnecting = false;
      });
  },
});

/**
 * Fires a per-platform "add account" click event to both Amplitude (product
 * analytics) and HubSpot (sales/lifecycle) so RevOps can segment leads by
 * which platforms they tried to connect. Silently no-ops if the platform
 * has no entry in {@link trackEventNames}.
 */
async function trackSocialConnectEvent(accType: SocialAccountType) {
  const eventName = trackEventNames[accType];

  if (!eventName) {
    return;
  }

  try {
    await trackEventFx({
      acc_type: accType,
      eventName,
      useServices: ["AMP", "HS"],
    });
  } catch {
    // analytics failure must not block the connect flow
  }
}

async function handleConnectedAccount(
  thunkApi: {
    dispatch: (action: unknown) => unknown;
    getState: () => unknown;
  },
  newAccount: AccountType
) {
  onAccountAdded(newAccount);
  accountSelected(newAccount._id);
  thunkApi.dispatch(
    socialAccountConnectActions.setSelectedAccountId(newAccount._id)
  );

  const localStorageAddAccountToGroup = getLocalStorageAddAccountToGroup();
  const localStorageHomeSelectedGroupName =
    getLocalStorageHomeSelectedGroupName();

  if (localStorageAddAccountToGroup && localStorageHomeSelectedGroupName) {
    const groups = getAccountsGroups(thunkApi.getState() as ReduxRootState);
    const group = groups.find(
      (item) => item.name === localStorageHomeSelectedGroupName
    );

    if (group) {
      await thunkApi.dispatch(
        updateAccountGroups({
          id: group._id,
          accountGroupsData: {
            accountIds: [newAccount._id],
          },
        }) as never
      );
    }

    await Promise.all([
      refreshAccountsList(),
      thunkApi.dispatch(fetchAccountGroups() as never),
    ]);
  }

  if (getAccountPlatformType(newAccount) === "IG") {
    setLocalStorageAddAccountToGroup("");
    setTimeout(() => {
      thunkApi.dispatch(socialAccountConnectActions.openDialog("add-password"));
    }, 1000);
  } else {
    thunkApi.dispatch(socialAccountConnectActions.closeDialogState(true));
  }
}

export const startConnectingAccount = createAsyncThunk(
  "socialAccountConnect/startConnectingAccount",
  async (
    payload: {
      acc_type: SocialAccountType;
      preferences?: Record<string, unknown>;
    },
    thunkApi
  ) => {
    const { acc_type } = payload;

    void trackSocialConnectEvent(acc_type);

    switch (acc_type) {
      case SocialAccountType.Instagram:
      case SocialAccountType.Youtube:
      case SocialAccountType.FacebookGroup:
      case SocialAccountType.Snapchat:
      case SocialAccountType.ThreadsNotification:
      case SocialAccountType.Other: {
        thunkApi.dispatch(socialAccountConnectActions.openDialog(acc_type));
        return;
      }
      case SocialAccountType.InstagramPersonal: {
        window.open(
          "https://help.skedsocial.com/how-do-i-connect-a-personal-instagram-account",
          "_blank"
        );
        return;
      }
      case SocialAccountType.ThreadsConnect: {
        await redirectTo(
          redirectToThreadsAuthPage({
            isReconnect: false,
          })
        );
        thunkApi.dispatch(socialAccountConnectActions.closeDialogState(false));
        return;
      }
      case SocialAccountType.FacebookPage: {
        await thunkApi.dispatch(fetchFacebookPagesThunk() as never);
        thunkApi.dispatch(
          socialAccountConnectActions.openDialog(SocialAccountType.FacebookPage)
        );
        return;
      }
      case SocialAccountType.PinterestV3: {
        await redirectTo(getPinterestRedirect(false, true));
        thunkApi.dispatch(socialAccountConnectActions.closeDialogState(false));
        return;
      }
      case SocialAccountType.Pinterest: {
        await redirectTo(getPinterestRedirect(false));
        thunkApi.dispatch(socialAccountConnectActions.closeDialogState(false));
        return;
      }
      case SocialAccountType.TikTok: {
        await redirectTo(getTikTokRedirect(false));
        thunkApi.dispatch(socialAccountConnectActions.closeDialogState(false));
        return;
      }
      case SocialAccountType.Twitter: {
        await redirectTo(await getTwitterRedirect());
        thunkApi.dispatch(socialAccountConnectActions.closeDialogState(false));
        return;
      }
      case SocialAccountType.GoogleMyBusiness: {
        try {
          const locations = await getAvailableGmbLocations({
            throwOnEmpty: true,
          });
          thunkApi.dispatch(
            socialAccountConnectActions.setGmbLocationsState(locations)
          );
          thunkApi.dispatch(
            socialAccountConnectActions.setGmbLoggingInState(false)
          );
          thunkApi.dispatch(
            socialAccountConnectActions.openDialog(
              SocialAccountType.GoogleMyBusiness
            )
          );
        } catch {
          thunkApi.dispatch(
            socialAccountConnectActions.setGmbLoggingInState(true)
          );
          await redirectTo(await getGmbRedirect());
          thunkApi.dispatch(
            socialAccountConnectActions.closeDialogState(false)
          );
        }
        return;
      }
      case SocialAccountType.LinkedInCompany:
      case SocialAccountType.LinkedInPersonal: {
        try {
          const profiles = await getAvailableLinkedInProfiles({
            throwOnEmpty: true,
          });
          thunkApi.dispatch(
            socialAccountConnectActions.setLinkedinProfilesState(profiles)
          );
          thunkApi.dispatch(
            socialAccountConnectActions.setLinkedinLoggingInState(false)
          );
          thunkApi.dispatch(
            socialAccountConnectActions.openDialog(
              SocialAccountType.LinkedInPersonal
            )
          );
        } catch {
          thunkApi.dispatch(
            socialAccountConnectActions.setLinkedinLoggingInState(true)
          );
          await redirectTo(await getLinkedInRedirect());
          thunkApi.dispatch(
            socialAccountConnectActions.closeDialogState(false)
          );
        }
        return;
      }
      default: {
        thunkApi.dispatch(socialAccountConnectActions.closeDialogState(true));
      }
    }
  }
);

export const initSocialAccountConnectFromQueryParams = createAsyncThunk(
  "socialAccountConnect/initFromQueryParams",
  async (params: SocialAccountConnectQueryParams, thunkApi) => {
    if (params.connected) {
      void refreshAccountsAfterRedirect(thunkApi, params.accountId).catch(
        () => {
          // Ignore background refresh failures. The user-facing signal for this
          // flow is the redirect toast, and account loading will retry on the
          // next entry point that refreshes accounts.
        }
      );
      toast.success("Account successfully connected.");
      return;
    }

    if (params.reconnected) {
      void refreshAccountsAfterRedirect(thunkApi, params.accountId).catch(
        () => {
          // Ignore background refresh failures for the reconnect redirect too.
        }
      );
      toast.success("Account successfully reconnected.");
      return;
    }

    if (params.reconnectedFromNotifications) {
      switch (params.reconnectedFromNotifications.toLocaleUpperCase()) {
        case PlatformNameToPlatformTypeMap.tiktok: {
          thunkApi.dispatch(
            socialAccountConnectActions.openDialog(SocialAccountType.TikTok)
          );
          return;
        }
        case PlatformNameToPlatformTypeMap.threads: {
          thunkApi.dispatch(
            socialAccountConnectActions.openDialog(
              SocialAccountType.ThreadsConnect
            )
          );
          return;
        }
        default: {
          return;
        }
      }
    }

    if (params.addLinkedin) {
      await thunkApi.dispatch(continueLiAccountAdding() as never);
      thunkApi.dispatch(
        socialAccountConnectActions.openDialog(
          SocialAccountType.LinkedInPersonal
        )
      );
      return;
    }

    if (params.addGmb) {
      await thunkApi.dispatch(continueGmbAccountAdding() as never);
      thunkApi.dispatch(
        socialAccountConnectActions.openDialog(
          SocialAccountType.GoogleMyBusiness
        )
      );
      return;
    }

    if (params.addYt) {
      thunkApi.dispatch(
        socialAccountConnectActions.openDialog(SocialAccountType.Youtube)
      );
      return;
    }

    if (params.error) {
      toast.error(params.error, { autoClose: 10_000 });
    }
  }
);

export const fetchInstagramConnectAccounts = createAsyncThunk(
  "socialAccountConnect/fetchInstagramConnectAccounts",
  async (_, thunkApi) => {
    const user = selectCurrentUser(thunkApi.getState() as ReduxRootState);

    return await getInstagramAccounts({
      additionalScopes: [...InstagramScopeInboxComments],
      useShortPagesFbApi: user.preferences?.useShortPagesFbApi || false,
    });
  }
);

export const connectInstagramAccount = createAsyncThunk(
  "socialAccountConnect/connectInstagramAccount",
  async (
    payload: {
      session: InstagramSession;
      page: FBPageWithInsta;
    },
    thunkApi
  ) => {
    try {
      const account = await addInstagramAccount(payload);

      thunkApi.dispatch(addAccount(account));
      toast.success(
        `${getAccountDisplayName(account)} has been successfully added to your accounts`
      );
      await handleConnectedAccount(thunkApi, account);

      return account;
    } catch (error) {
      showFriendlyError(error);
      throw error;
    }
  }
);

export const fetchFacebookPagesThunk = createAsyncThunk(
  "socialAccountConnect/fetchFacebookPages",
  async (_, thunkApi) => {
    const user = selectCurrentUser(thunkApi.getState() as ReduxRootState);

    return await getFacebookPages({
      user,
    } as never);
  }
);

export const connectFacebookPage = createAsyncThunk(
  "socialAccountConnect/connectFacebookPage",
  async (
    payload: {
      session: FacebookSession;
      account: unknown;
      page: FBPageExtended;
    },
    thunkApi
  ) => {
    try {
      const account = await addFacebookPageAccount(payload as never);

      thunkApi.dispatch(addAccount(account));
      toast.success(
        `${account.login} has been successfully added to your accounts`
      );
      await handleConnectedAccount(thunkApi, account);

      return account;
    } catch (error) {
      showFriendlyError(error);
      throw error;
    }
  }
);

export const fetchFacebookGroupsThunk = createAsyncThunk(
  "socialAccountConnect/fetchFacebookGroups",
  async () => {
    try {
      return await getFacebookGroups();
    } catch (error) {
      showFriendlyError(error);
      throw error;
    }
  }
);

export const connectFacebookGroup = createAsyncThunk(
  "socialAccountConnect/connectFacebookGroup",
  async (
    payload: {
      session: FacebookSession;
      account: unknown;
      group: FBGroup;
    },
    thunkApi
  ) => {
    try {
      const account = await addFacebookGroupAccount(payload as never);

      thunkApi.dispatch(addAccount(account));
      toast.success(
        `${account.login} has been successfully added to your accounts`
      );
      await handleConnectedAccount(thunkApi, account);

      return account;
    } catch (error) {
      showFriendlyError(error);
      throw error;
    }
  }
);

export const fetchYoutubeChannels = createAsyncThunk(
  "socialAccountConnect/fetchYoutubeChannels",
  async () => {
    try {
      return await getAvailableYtChannels();
    } catch (error) {
      showFriendlyError(error);
      throw error;
    }
  }
);

export const connectYtChannel = createAsyncThunk(
  "socialAccountConnect/connectYtChannel",
  async (channel: YoutubeChannel, thunkApi) => {
    try {
      const account = await addYtChannel(channel);

      thunkApi.dispatch(addAccount(account));
      await handleConnectedAccount(thunkApi, account);
      navigateToAccountsFx();

      return account;
    } catch (error) {
      showFriendlyError(error);
      throw error;
    }
  }
);

export const continueGmbAccountAdding = createAsyncThunk(
  "socialAccountConnect/continueGmbAccountAdding",
  async () => {
    try {
      return await getAvailableGmbLocations({ throwOnEmpty: false });
    } catch (error) {
      showFriendlyError(error);
      throw error;
    }
  }
);

export const addGmbLocationThunk = createAsyncThunk(
  "socialAccountConnect/addGmbLocation",
  async (location: GmbLocation, thunkApi) => {
    try {
      const account = await addGmbLocation(location);

      thunkApi.dispatch(addAccount(account));
      await handleConnectedAccount(thunkApi, account);

      return account;
    } catch (error) {
      showFriendlyError(error);
      throw error;
    }
  }
);

export const continueLiAccountAdding = createAsyncThunk(
  "socialAccountConnect/continueLiAccountAdding",
  async () => {
    try {
      return await getAvailableLinkedInProfiles({ throwOnEmpty: false });
    } catch (error) {
      showFriendlyError(error);
      throw error;
    }
  }
);

export const addLinkedInProfileThunk = createAsyncThunk(
  "socialAccountConnect/addLinkedInProfile",
  async (profile: LinkedInProfile, thunkApi) => {
    try {
      const account = await addLinkedInProfile(profile);

      thunkApi.dispatch(addAccount(account));
      await handleConnectedAccount(thunkApi, account);

      return account;
    } catch (error) {
      showFriendlyError(error);
      throw error;
    }
  }
);

export const uploadNotificationProfilePicture = createAsyncThunk(
  "socialAccountConnect/uploadNotificationProfilePicture",
  async (file: File) => {
    const blob = await getImageFromFile(file);
    return blob;
  }
);

export const connectNotificationAccount = createAsyncThunk(
  "socialAccountConnect/connectNotificationAccount",
  async (_, thunkApi) => {
    const state = getFeatureState(thunkApi.getState() as ReduxRootState);
    const { userName, profilePictureFile, type, platformTypeOther } =
      state.notification;

    let profilePictureUrl = "";

    if (profilePictureFile) {
      profilePictureUrl = await uploadFileToS3({
        blob: profilePictureFile,
        fileName: profilePictureFile.name,
      });
    }

    try {
      let account: AccountType;

      switch (type) {
        case "threads": {
          account = await connectThreadsNotificationAccount({
            login: userName,
            profilePictureUrl,
          });
          break;
        }
        case "tiktok": {
          account = await connectTikTokAccount({
            login: userName,
            profilePictureUrl,
          });
          break;
        }
        case "snapchat": {
          account = await connectSnapChatAccount({
            login: userName,
            profilePictureUrl,
          });
          break;
        }
        default: {
          account = await connectOtherAccount({
            login: userName,
            profilePictureUrl,
            platformTypeOther,
          });
        }
      }

      thunkApi.dispatch(addAccount(account));
      await handleConnectedAccount(thunkApi, account);

      return account;
    } catch (error) {
      showFriendlyError(error);
      throw error;
    }
  }
);

export const socialAccountConnectReducer = socialAccountConnectSlice.reducer;
export const socialAccountConnectActions = socialAccountConnectSlice.actions;

export const onAccountAdded = createWatchableEvent<AccountType>();
export const accountSelected = createWatchableEvent<string>();

export const selectSocialAccountConnectState = createSelector(
  getFeatureState,
  (state) => state
);

export const selectSocialAccountConnectDialog = createSelector(
  selectSocialAccountConnectState,
  (state) => state.currentDialog
);

export const selectSocialAccountConnectPending = createSelector(
  selectSocialAccountConnectState,
  (state) => state.startConnectingPending
);

export const selectSocialConnectInstagramState = createSelector(
  selectSocialAccountConnectState,
  (state) => state.instagram
);

export const selectSocialConnectYoutubeState = createSelector(
  selectSocialAccountConnectState,
  (state) => state.youtube
);

export const selectSocialConnectFacebookPageState = createSelector(
  selectSocialAccountConnectState,
  (state) => state.facebookPage
);

export const selectSocialConnectFacebookGroupState = createSelector(
  selectSocialAccountConnectState,
  (state) => state.facebookGroup
);

export const selectSocialConnectGmbState = createSelector(
  selectSocialAccountConnectState,
  (state) => state.gmb
);

export const selectSocialConnectLinkedinState = createSelector(
  selectSocialAccountConnectState,
  (state) => state.linkedin
);

export const selectSocialConnectNotificationState = createSelector(
  selectSocialAccountConnectState,
  (state) => state.notification
);

export const selectShowHowdyGoDemoDialog = createSelector(
  selectSocialAccountConnectState,
  (state) => state.showHowdyGoDemoDialog
);

export const selectIsMainDialogGuarded = createSelector(
  selectSocialAccountConnectState,
  (state) => state.isMainDialogGuarded
);

export const selectSelectedConnectedAccount = createSelector(
  selectSocialAccountConnectState,
  selectAllAccounts,
  (state, accounts) =>
    accounts.find((account) => account._id === state.selectedAccountId) ?? null
);

export const selectSocialAccountConnectView = createSelector(
  selectSocialAccountConnectState,
  selectAllAccounts,
  getAccountsCounts,
  (state, existingAccounts, counters) => ({
    currentDialog: state.currentDialog,
    isLoading: state.startConnectingPending,
    existingAccounts,
    counters,
    showHowdyGoDemoDialog: state.showHowdyGoDemoDialog,
    isMainDialogGuarded: state.isMainDialogGuarded,
    selectedAccountId: state.selectedAccountId,
  })
);

export function openDialog(dialog: CurrentDialog) {
  dispatchAppAction(socialAccountConnectActions.openDialog(dialog));
}

export function closeDialog(clearLocalStorage?: boolean) {
  dispatchAppAction(
    socialAccountConnectActions.closeDialogState(clearLocalStorage)
  );
}

export const openSocialAccountConnectDialog = () => openDialog("main");

export function openHowdyGoDemoDialog() {
  dispatchAppAction(socialAccountConnectActions.openHowdyGoDemoDialogState());
}

export function closeHowdyGoDemoDialog() {
  dispatchAppAction(socialAccountConnectActions.closeHowdyGoDemoDialogState());
}

export function enableMainDialogGuard() {
  dispatchAppAction(socialAccountConnectActions.enableMainDialogGuardState());
}

export function disableMainDialogGuard() {
  dispatchAppAction(socialAccountConnectActions.disableMainDialogGuardState());
}

export function startConnectingAccountFx(payload: {
  acc_type: SocialAccountType;
  preferences?: Record<string, unknown>;
}) {
  return dispatchAppAction(startConnectingAccount(payload));
}

export function initSocialAccountConnectFromQueryParamsFx(
  payload: SocialAccountConnectQueryParams
) {
  return dispatchAppAction(initSocialAccountConnectFromQueryParams(payload));
}

export function getAccountsFx() {
  return dispatchAppAction(fetchInstagramConnectAccounts());
}

export function addInstagramAccountFx(payload: {
  session: InstagramSession;
  page: FBPageWithInsta;
}) {
  return dispatchAppAction(connectInstagramAccount(payload));
}

export function getPagesFx() {
  return dispatchAppAction(fetchFacebookPagesThunk());
}

export function addFacebookPageAccountFx(payload: {
  session: FacebookSession;
  account: unknown;
  page: FBPageExtended;
}) {
  return dispatchAppAction(connectFacebookPage(payload));
}

export function getGroupsFx() {
  return dispatchAppAction(fetchFacebookGroupsThunk());
}

export function addFacebookGroupAccountFx(payload: {
  session: FacebookSession;
  account: unknown;
  group: FBGroup;
}) {
  return dispatchAppAction(connectFacebookGroup(payload));
}

export async function loginWithGoogleFx() {
  dispatchAppAction(socialAccountConnectActions.setYoutubeLoggingInState(true));
  await redirectTo(await getYtRedirect());
}

export async function loginWithGmbFx() {
  dispatchAppAction(socialAccountConnectActions.setGmbLoggingInState(true));
  await redirectTo(await getGmbRedirect());
}

export function navigateToAccountsFx() {
  window.history.replaceState(
    null,
    window.document.title,
    "/dashboard/accounts"
  );
}

export function addYtChannelFx(channel: YoutubeChannel) {
  return dispatchAppAction(connectYtChannel(channel));
}

export function fetchYoutubeChannelsFx() {
  return dispatchAppAction(fetchYoutubeChannels());
}

export async function startGmbAccountAddingFx({
  onRedirect,
  onData,
}: {
  onRedirect(): void;
  onData(): void;
}) {
  try {
    const locations = await getAvailableGmbLocations({ throwOnEmpty: true });
    dispatchAppAction(
      socialAccountConnectActions.setGmbLocationsState(locations)
    );
    dispatchAppAction(socialAccountConnectActions.setGmbLoggingInState(false));
    onData();
  } catch {
    dispatchAppAction(socialAccountConnectActions.setGmbLoggingInState(true));
    await redirectTo(await getGmbRedirect());
    onRedirect();
  }
}

export function continueGmbAccountAddingFx() {
  return dispatchAppAction(continueGmbAccountAdding());
}

export function addGmbLocationFx(location: GmbLocation) {
  return dispatchAppAction(addGmbLocationThunk(location));
}

export async function startLiAccountAddingFx({
  onRedirect,
  onData,
}: {
  onRedirect(): void;
  onData(): void;
}) {
  try {
    const profiles = await getAvailableLinkedInProfiles({ throwOnEmpty: true });
    dispatchAppAction(
      socialAccountConnectActions.setLinkedinProfilesState(profiles)
    );
    dispatchAppAction(
      socialAccountConnectActions.setLinkedinLoggingInState(false)
    );
    onData();
  } catch {
    dispatchAppAction(
      socialAccountConnectActions.setLinkedinLoggingInState(true)
    );
    await redirectTo(await getLinkedInRedirect());
    onRedirect();
  }
}

export async function loginWithLinkedInFx() {
  dispatchAppAction(
    socialAccountConnectActions.setLinkedinLoggingInState(true)
  );
  await redirectTo(await getLinkedInRedirect());
}

export function continueLiAccountAddingFx() {
  return dispatchAppAction(continueLiAccountAdding());
}

export function addLinkedInProfileFx(profile: LinkedInProfile) {
  return dispatchAppAction(addLinkedInProfileThunk(profile));
}

export function userNameChanged(value: string) {
  dispatchAppAction(socialAccountConnectActions.setNotificationUserName(value));
}

export function profilePictureFileSelected(file: File) {
  dispatchAppAction(
    socialAccountConnectActions.setNotificationProfilePictureFile(file)
  );
  dispatchAppAction(uploadNotificationProfilePicture(file));
}

export function profilePictureDeleted() {
  dispatchAppAction(
    socialAccountConnectActions.setNotificationProfilePictureFile(null)
  );
  dispatchAppAction(
    socialAccountConnectActions.setNotificationProfilePictureUrl("")
  );
}

export function platformTypeOtherChanged(value: string) {
  dispatchAppAction(
    socialAccountConnectActions.setNotificationPlatformTypeOther(value)
  );
}

export function formSubmitted() {
  return dispatchAppAction(connectNotificationAccount());
}

export function updateMaxAllowed(count: number) {
  const store = resolveReduxAppStore();

  if (!store) {
    return;
  }

  const state = store.getState() as ReduxRootState;
  const counts = getAccountsCounts(state);

  store.dispatch(
    setAccountsCounts({
      ...counts,
      maxAllowed: count,
    })
  );
}

export async function addAccountToGroupIfNeededAndCloseDialogFx(
  newAccount: AccountType
) {
  const store = resolveReduxAppStore();

  if (!store) {
    return;
  }

  await handleConnectedAccount(store as never, newAccount);
}
